// A23 — Harness Observability. Reads runner_session + harness_score.
// Three executor lanes (native_runner, subscription_cli,
// observed_external) all emit into the same evidence envelope; this
// panel surfaces their attribution + score axes.

import { useEffect, useState } from "react";
import {
  ApiError,
  listHarnessScores,
  listRunnerSessions,
  type HarnessScoreRow,
  type RunnerSessionRow,
} from "../api/client";

interface Props {
  workspaceId: number;
}

export function HarnessScoresPanel({ workspaceId }: Props) {
  const [sessions, setSessions] = useState<RunnerSessionRow[]>([]);
  const [scores, setScores] = useState<HarnessScoreRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setError(null);
    Promise.all([listRunnerSessions(workspaceId), listHarnessScores(workspaceId)])
      .then(([s, h]) => {
        if (cancelled) return;
        setSessions(s);
        setScores(h);
      })
      .catch((e) =>
        setError(e instanceof ApiError ? e.message : String(e)),
      )
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  return (
    <section
      className="flex flex-col gap-3 rounded-md border border-edge bg-surface-raised p-3"
      data-testid="harness-scores-panel"
    >
      <header>
        <h3 className="text-xs font-semibold text-primary">
          Harness lanes + scores
        </h3>
        <p className="mt-1 text-[0.625rem] text-muted">
          Three executor lanes share one evidence envelope. Scores come
          from real runs — no opinions, just receipt counts.
        </p>
      </header>
      {busy && <p className="text-[0.625rem] text-muted">Loading…</p>}
      {error && <p className="text-[0.625rem] text-danger">{error}</p>}

      <section>
        <h4 className="text-[0.625rem] font-medium text-muted">Runner sessions</h4>
        {sessions.length === 0 ? (
          <p className="text-[0.625rem] text-faint">No active sessions.</p>
        ) : (
          <ul className="mt-1 flex flex-col gap-1">
            {sessions.slice(0, 20).map((s) => (
              <li
                key={s.session_id}
                className="rounded-md border border-edge bg-surface px-2 py-1"
                data-testid={`runner-session-${s.session_id}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-code text-[0.625rem] text-primary">
                    {s.executor_lane} · {s.harness_version || "?"}
                  </span>
                  <span
                    className={`font-code text-[0.625rem] ${
                      s.status === "running"
                        ? "text-warning"
                        : s.status === "complete"
                          ? "text-success"
                          : "text-danger"
                    }`}
                  >
                    {s.status}
                  </span>
                </div>
                <p className="mt-0.5 text-[0.625rem] text-muted">
                  turn #{s.turn_id ?? "—"} · last hb{" "}
                  {new Date(s.last_heartbeat_at / 1000).toLocaleTimeString()}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h4 className="text-[0.625rem] font-medium text-muted">Harness scores</h4>
        {scores.length === 0 ? (
          <p className="text-[0.625rem] text-faint">
            No scores yet — they accumulate as Shore writes harness deltas
            from gate evidence.
          </p>
        ) : (
          <table className="w-full text-[0.625rem]">
            <thead className="text-faint">
              <tr>
                <th className="text-left">lane</th>
                <th className="text-left">tag</th>
                <th className="text-right">evidence</th>
                <th className="text-right">cost vis</th>
                <th className="text-right">cmd ctrl</th>
                <th className="text-right">artefacts</th>
                <th className="text-right">recovery</th>
                <th className="text-right">n</th>
              </tr>
            </thead>
            <tbody className="font-code">
              {scores.slice(0, 50).map((s) => (
                <tr
                  key={s.score_id}
                  data-testid={`harness-score-${s.score_id}`}
                >
                  <td>{s.executor_lane}</td>
                  <td>{s.task_tag}</td>
                  <td className="text-right">{Math.round(s.evidence_completeness * 100)}%</td>
                  <td className="text-right">{Math.round(s.cost_visibility * 100)}%</td>
                  <td className="text-right">{Math.round(s.command_control * 100)}%</td>
                  <td className="text-right">{Math.round(s.artefact_capture * 100)}%</td>
                  <td className="text-right">{Math.round(s.failure_recovery * 100)}%</td>
                  <td className="text-right">{s.sample_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </section>
  );
}
